// src/utils/earlyWarning.js
//
// THE EARLY-WARNING LAYER — pure JavaScript, no React, no Firebase.
//
// The snapshot says where each group stands. This layer says which way it is
// moving, how fast, what is driving it, and what to do next, from the aggregate
// fields getEnterpriseSnapshot added on 2026-11-03: groups[].prior (the same
// state as of four weeks ago), groups[].drivers (the share at moderate or worse
// on each screener) and groups[].movement (each person's latest check-in
// against their previous one, aggregated), with the same three org-wide.
// Everything is read at the privacy floor the server already applied; nothing
// here describes a person. The one modeled figure is the pace reading: a
// straight line from four weeks ago to today, read against the population
// line, and said to be modeled wherever it is shown.
//
// Model documentation: docs/EXECUTIVE-METRICS.md ("Direction, drivers and movement").

// Share screening positive among Canadian adults, spring 2021 (Statistics
// Canada, Survey on COVID-19 and Mental Health): the reference the cohort
// panel prints (RunningHotPanel's POPULATION_SCREEN_REFERENCE).
export const POPULATION_LINE = 0.25;

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
export const pts = (share) => (share === null || share === undefined ? null : Math.round(share * 100));

// Screening positive is the two cohorts together (see RunningHotPanel's header).
export const screenShare = (state) => {
    if (!state) return null;
    const ns = num(state.needsSupportShare);
    const rh = num(state.runningHotShare);
    return (ns === null && rh === null) ? null : (ns || 0) + (rh || 0);
};

// Direction: today's share against the state as of four weeks ago, in points.
export const momentumFor = (group, windowDays = 28) => {
    const now = screenShare(group);
    if (now === null) return null;
    const weeks = Math.max(1, Math.round(windowDays / 7));
    const prior = screenShare(group.prior);
    if (prior === null) return { now, prior: null, deltaPts: null, perWeekPts: null, weeks };
    const deltaPts = pts(now) - pts(prior);
    return { now, prior, deltaPts, perWeekPts: deltaPts / weeks, weeks };
};

// Direction: where the group stands against the population mark (the share of
// Canadian adults who screen positive) and which way it is moving, as a short
// label and a plain sentence. A straight line through the two points gives the
// weeks to the mark; a point a week is movement, less is holding. Modeled, and
// labelled so wherever it is shown.
export const paceFor = (momentum, line = POPULATION_LINE) => {
    if (!momentum) return null;
    const { now, deltaPts, perWeekPts } = momentum;
    const mark = `${pts(line)}%`;
    const rate = perWeekPts === null ? null : Math.abs(Math.round(perWeekPts * 10) / 10);
    if (now >= line) {
        const trend = deltaPts === null ? '' : perWeekPts >= 1 ? ', and still rising' : perWeekPts <= -1 ? ', and easing' : ', and holding there';
        return { kind: 'above', weeksTo: 0, label: `Over the ${mark} mark`, detail: `Already above the ${mark} of Canadian adults who screen positive${trend}` };
    }
    if (deltaPts === null) return { kind: 'unknown', weeksTo: null, label: 'Too early to read', detail: 'Direction needs four weeks of check-ins' };
    if (perWeekPts >= 1) {
        const weeksTo = Math.ceil((pts(line) - pts(now)) / perWeekPts);
        return { kind: 'rising', weeksTo, label: `Heading to the ${mark} mark`, detail: `Rising about ${rate} pts a week; reaches ${mark} in about ${weeksTo} week${weeksTo === 1 ? '' : 's'} at this pace` };
    }
    if (perWeekPts <= -1) return { kind: 'easing', weeksTo: null, label: `Easing, under ${mark}`, detail: `Under the ${mark} mark and falling about ${rate} pts a week` };
    return { kind: 'steady', weeksTo: null, label: `Holding, under ${mark}`, detail: `Under the ${mark} mark and moving less than a point a week` };
};

// What is driving it: the screener with the largest share at moderate or worse,
// when at least one in ten carries it. When no screener reaches one in ten (the
// positives are spread thin across different screeners), the strain that exists
// may still be mostly the hidden kind: the Running Hot pattern leads when one in
// ten or more are Running Hot and that cohort is at least as large as Needs
// Support. Otherwise nothing leads, and the step is to hold. The divergence rate
// is not used here: it fires in both directions (output low while wellbeing is
// fine as much as the reverse), so it does not name the Running Hot pattern.
export const DRIVER_LABELS = {
    anxiety: 'Anxiety', depression: 'Low mood', sleep: 'Sleep', focus: 'Focus and attention',
    trauma: 'Trauma exposure', divergence: 'Running Hot pattern'
};
export const DRIVER_NOTES = {
    anxiety: 'GAD-7 at 10 or more', depression: 'PHQ-9 at 10 or more',
    sleep: 'sleep disturbance at moderate or worse', focus: 'ASRS-6 at 12 or more',
    trauma: 'PC-PTSD-5 at 3 or more', divergence: 'output high while wellbeing falls'
};
export const leadingDriverFor = (drivers, state) => {
    if (!drivers) return null;
    const ranked = ['anxiety', 'depression', 'sleep', 'focus', 'trauma']
        .map((key) => ({ key, share: num(drivers[key]) }))
        .filter((d) => d.share !== null)
        .sort((a, b) => b.share - a.share);
    const top = ranked[0];
    if (top && top.share >= 0.1) return { key: top.key, share: top.share, label: DRIVER_LABELS[top.key], note: DRIVER_NOTES[top.key] };
    const rh = num(state && state.runningHotShare);
    const ns = num(state && state.needsSupportShare);
    if (rh !== null && rh >= 0.1 && rh >= (ns || 0)) return { key: 'divergence', share: rh, label: DRIVER_LABELS.divergence, note: DRIVER_NOTES.divergence };
    return null;
};

// What to do next, by driver. Workplace wording by default; an edition says it
// in its own terms through edition.earlyWarning.steps[key].
export const DEFAULT_NEXT_STEPS = {
    anxiety: 'Workload and recovery first: the workload-and-recovery guide to the group’s leads, and managers opening their check-ins with it this month.',
    depression: 'Route to support now: the assistance program and Talk to Someone in front of the group this week, through the people they already trust.',
    sleep: 'Sleep and shift recovery: the sleep content to the group, and a look at schedules and rest between shifts.',
    focus: 'Focus and attention: the focus-and-recovery content to the group, and a look at interruptions, hours and rest across the week.',
    trauma: 'After difficult events: peer support and the after-a-hard-day guide to the group, and a debrief that does not get bumped.',
    divergence: 'Running Hot looks fine from the outside: a manager conversation guide to the group’s leads, and a shorter check-in cadence until it eases.',
    none: 'Hold steady: keep the cadence and watch the four-week change.'
};
export const nextStepFor = (driver, edition) => {
    const key = driver ? driver.key : 'none';
    const steps = (edition && edition.earlyWarning && edition.earlyWarning.steps) || {};
    return steps[key] || DEFAULT_NEXT_STEPS[key];
};

// Does the snapshot carry direction at all? (The function deployed 2026-11-03;
// the modeled example and an older snapshot carry none, and the panels stay quiet.)
export const hasDirection = (live) => !!(live && live.source === 'live' && (live.groups || []).some((g) => g && (g.prior || g.drivers || g.movement)));

// One row per visible group, the fastest-rising first.
export const earlyWarningRows = (live, edition) => {
    if (!live || live.source !== 'live') return [];
    const windowDays = num(live.momentumWindowDays) || 28;
    return (live.groups || [])
        .filter((g) => g && !g.suppressed && screenShare(g) !== null)
        .map((g) => {
            const momentum = momentumFor(g, windowDays);
            const driver = leadingDriverFor(g.drivers, g);
            return {
                name: g.name, responses: g.responses, momentum, pace: paceFor(momentum), driver,
                movement: g.movement || null, nextStep: nextStepFor(driver, edition)
            };
        })
        .sort((a, b) => ((b.momentum.deltaPts ?? -1e9) - (a.momentum.deltaPts ?? -1e9)) || (b.momentum.now - a.momentum.now));
};

// The organization's own, for the forecast tab.
export const orgEarlyWarning = (live, edition) => {
    if (!live || live.source !== 'live') return null;
    const windowDays = num(live.momentumWindowDays) || 28;
    const q = live.quadrants || {};
    const state = { needsSupportShare: num(q.needs_support), runningHotShare: num(q.running_hot), prior: live.prior || null };
    const momentum = momentumFor(state, windowDays);
    if (!momentum) return null;
    const driver = leadingDriverFor(live.drivers, state);
    return { momentum, pace: paceFor(momentum), driver, movement: live.movement || null, nextStep: nextStepFor(driver, edition) };
};

// The sentence at the top of the Overview, from the fastest-rising group.
export const headlineFor = (rows) => {
    const top = rows && rows[0];
    if (!top) return null;
    const m = top.momentum;
    const parts = [];
    if (m.deltaPts !== null && Math.abs(m.deltaPts) >= 3) {
        parts.push(`${top.name} ${m.deltaPts > 0 ? 'rose' : 'fell'} ${Math.abs(m.deltaPts)} points in ${m.weeks} weeks, to ${pts(m.now)}% screening positive`);
    } else {
        parts.push(`${top.name} stands at ${pts(m.now)}% screening positive`);
    }
    if (top.movement && top.movement.newlyPositive !== null && top.movement.newlyPositive !== undefined) {
        parts.push(`${pts(top.movement.newlyPositive)}% of its respondents turned positive since their previous check-in`);
    }
    const text = `${parts.join('; ')}.${top.driver ? ` ${top.driver.label} leads.` : ''}`;
    return { group: top.name, text, nextStep: top.nextStep, pace: top.pace, driver: top.driver };
};
